import { patchState, signalStore, signalStoreFeature, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { computed, effect, inject, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, firstValueFrom, noop, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import {
  createNewVoucherWarning,
  getSessionStorageName,
  ILError,
  NewVoucherIdentifier,
  NewVoucherWarning,
  ValidityObject,
} from './new-voucher.util';
import { isEqual } from '@mwe/utils';
import { EnvironmentService, LoadingService, LoggingService, NewVoucherService, productStore } from '@mwe/services';
import { Aktionscode, GueltigkeitsDetails, IAktionen, Product, TariffSelectionCardData } from '@mwe/models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TariffSelectionStore } from '@mwe/ui/components';

export type NewVoucherStoreType = InstanceType<typeof NewVoucherStore>;

type NewVoucherStore = typeof initState;

/*
Complicated logic of this abomination:

  Client starts (WARNING)
  Client sends code --- (WARNING) ---> Server
  Server answers --- (ERROR) ---> Client
  Client reacts (WARNING)
  Client sends code and selected tarif --- () ---> Server
  Server answers --- (ERROR) ---> Client
  Client reacts ()
  Client sends code from possible new form --- () ---> Server
  Server answers --- (ERROR) ---> Client
  Client reacts ()

I believe most of this logic should have been handled in the backend, but oh well :C
*/

const initState = {
  // ===== HTTP response
  /** Detail of currently fetched voucher **/
  voucherDetail: undefined as Aktionscode,
  /** Error of currently fetched voucher **/
  voucherDetailError: undefined as ILError,
  /** Warning of to be fetched voucher **/
  voucherDetailWarning: undefined as NewVoucherWarning,
  /** Validity of given voucher **/
  voucherValidity: undefined as GueltigkeitsDetails,
  /** Error of currently fetched voucher with associated body **/
  voucherValidityError: undefined as ILError,

  // ===== Wizard values
  /** Voucher to be used in other processes **/
  voucher: undefined as IAktionen,
  /** Current validity - use valid(), firstValueFrom(isValid$) or preisindikationenAPICall() instead **/
  isNewValid: { value: undefined } as ValidityObject,
  /** Current visibility of new voucher form in Alles-Sicher process **/
  isVisible: false as boolean,
  /** To indicate if isVisible can be changed as it should only be set once **/
  isVisibilityBlocked: false as boolean,

  // ===== Util values
  /** To indicate the current tariff for which the HTTP request was made **/
  selectedTariff: undefined as TariffSelectionCardData,
  /** Tariff keys that have successfully passed voucher validation **/
  validatedTariffKeys: [] as string[],
  /** Dummy value to set invalid form **/
  setError: undefined as boolean,
  /** Dummy value to set invalid Wiener Linien form **/
  setWLError: undefined as boolean, // boolean is undefined and checked for because of a weird form.control bug
  /** For a very specific sequence and unique initialization **/
  counter: 0 as number,
};

// this work-around is necessary as the key must be dynamically generated
const withStorageSyncDynamic = signalStoreFeature(store => {
  const withStorageSyncDynamic = withStorageSync(
    {
      key: getSessionStorageName(store.props['environmentService'].getPortalCustomerType()),
      // type NewVoucherStore is not possible because of unit tests
      select: (state: object): Partial<NewVoucherStore> => ({ voucher: state['voucher'] }),
    },
    withSessionStorage(),
  );
  return withStorageSyncDynamic(store);
});

export const NewVoucherStore = signalStore(
  { providedIn: 'root' },
  withState<NewVoucherStore>(initState),
  withProps(() => ({
    newVoucherService: inject(NewVoucherService),
    productStore: inject(productStore), // used to calculate eligibility
    tariffSelectionStore: inject(TariffSelectionStore), // used in Tarifwechsel process
    environmentService: inject(EnvironmentService),
    loggingService: inject(LoggingService),
    loadingService: inject(LoadingService),
  })),
  withStorageSyncDynamic,
  withComputed(store => {
    const eligibleProducts = computed<Product[]>(() => {
      const voucherDetail = store.voucherDetail();
      const products = store.productStore.activeProducts();

      if (!voucherDetail) return [];
      if (products?.length === 0) return [];

      // get the filters from the voucher (for example "GE*")
      const tarifKeyFilters = voucherDetail?.aktion?.zulaessigeTarife?.map(tarif =>
        tarif?.tarifKeyFilter ? new RegExp('^' + tarif.tarifKeyFilter.replace('*', '.*') + '$') : undefined,
      );

      return products.filter(product => tarifKeyFilters?.some(filter => filter?.test(product?.tarifKey)));
    });

    let prevSelectedTariffsForNewVoucher: TariffSelectionCardData[];
    const preisindikationenAPICall = computed<ValidityObject>(() => store.isNewValid(), {
      equal: (prevObject, currObject) => {
        const prev = prevObject.value;
        const curr = currObject.value;

        // skip if it is loading
        if (curr === null) return true;

        const currSelectedTariffsForNewVoucher = untracked(() => store.tariffSelectionStore.selectedTariffsForNewVoucher());

        const hasOneTariffChanged = prevSelectedTariffsForNewVoucher
          ? prevSelectedTariffsForNewVoucher.some(x =>
              currSelectedTariffsForNewVoucher.some(
                y => x.anlageId === y.anlageId && x.tariffKey === y.tariffKey && !isEqual(x.userSelection, y.userSelection),
              ),
            )
          : prevSelectedTariffsForNewVoucher;

        prevSelectedTariffsForNewVoucher = currSelectedTariffsForNewVoucher;

        // skip the first run
        if (hasOneTariffChanged === undefined) return true;
        // if tarif changed, check if there is a need to redo the call
        if (hasOneTariffChanged) return Object.is(prevObject, currObject);

        // hardcoded special cases
        // TODO: Fix - Navigating from another wizard-step to tarif selection makes 2 HTTP requests
        //  occurs when (prev === true && curr === undefined)

        return (
          (prev === undefined && curr === undefined) ||
          (prev === undefined && curr === false) ||
          (prev === false && curr === undefined) ||
          (prev === false && curr === false) ||
          Object.is(prevObject, currObject)
        );
      },
    });

    return {
      eligibleProducts,
      preisindikationenAPICall,
    };
  }),
  withMethods(store => {
    /** ===== Service Functions ===== **/
    const getVoucherDetail = async (voucherCode: string, isEnergiegutschein: boolean): Promise<void> => {
      if (isEnergiegutschein && checkOldVoucher(voucherCode)) {
        await randomWait();
        updateVoucherDetail(undefined);
        return updateVoucherDetailWarning(createNewVoucherWarning('oldTitle', 'oldText'));
      }

      // firstValueFrom subscribes to the Observable and returns a Promise - return value should not be used
      // this is only done because the loading indicator of the form relies on Promises and doesn't accept signals or Observables
      return firstValueFrom<void>(
        store.loadingService.load<void>({
          identifier: NewVoucherIdentifier.VOUCHER_DETAIL,
          observables: {
            [NewVoucherIdentifier.VOUCHER_DETAIL]: store.newVoucherService.getVoucherDetail(voucherCode).pipe(
              tap(voucherDetail => {
                updateVoucherDetail(voucherDetail);
                updateVoucherDetails(voucherDetail);
              }),
              map(noop),
              catchError(error => {
                store.loggingService.logError(error, `ERROR: Could not retrieve details of voucher: ${voucherCode}`);
                updateVoucherDetailError(getILError(error));
                return of(null);
              }),
            ),
          },
        }),
      );
    };

    // same problem as with the function above
    const getVoucherValidity = async (voucherCode: string, body: string, selectedTariff?: TariffSelectionCardData): Promise<void> => {
      return firstValueFrom<void>(
        store.loadingService.load<void>({
          identifier: NewVoucherIdentifier.VOUCHER_VALIDITY,
          observables: {
            [NewVoucherIdentifier.VOUCHER_VALIDITY]: store.newVoucherService.getVoucherValidity(voucherCode, body).pipe(
              tap(voucherValidity => {
                if (selectedTariff) {
                  updateSelectedTariff(selectedTariff);
                  addValidatedTariffKey(selectedTariff.priceIndication?.tarif?.ISUTarifKey);
                }
                updateVoucherValidity(voucherValidity);
              }),
              map(noop),
              catchError(error => {
                store.loggingService.logError(error, `ERROR: Could not retrieve validity of voucher: ${voucherCode}`);
                const ILError = getILError(error);

                if (handleSpecialCases(ILError)) {
                  updateVoucherDetailError(ILError);
                  return of(null);
                }

                updateVoucherValidityError(ILError);
                return of(null);
              }),
            ),
          },
        }),
      );
    };

    /** ===== Store Functions ===== **/
    const resetStore = () => patchState(store, () => initState);
    const deleteStore = () => sessionStorage.removeItem(getSessionStorageName(store.environmentService.getPortalCustomerType()));
    const updateVoucherDetail = (voucherDetail: Aktionscode) => safePatchState('voucherDetail', voucherDetail);
    const updateVoucherDetailError = (voucherDetailError: ILError) => safePatchState('voucherDetailError', voucherDetailError);
    const updateVoucherDetailWarning = (voucherDetailWarning: NewVoucherWarning) =>
      safePatchState('voucherDetailWarning', voucherDetailWarning);
    const updateVoucherDetails = (voucherDetail: Aktionscode) => safePatchStateExtention('voucherDetails', voucherDetail);
    const updateVoucherValidity = (voucherValidity: GueltigkeitsDetails) => safePatchState('voucherValidity', voucherValidity);
    const updateVoucherValidityError = (voucherValidityError: ILError) => safePatchState('voucherValidityError', voucherValidityError);
    const updateVoucher = (voucher: IAktionen) => safePatchState('voucher', voucher);
    const updateIsNewValid = (_isNewValid: boolean): void => {
      const isNewValid = { value: _isNewValid } as ValidityObject;
      patchState(store, state => ({ ...state, isNewValid }));
    };
    const causeIsNewValidUpdate = (): void => {
      const isNewValid = untracked(() => store.isNewValid());
      safePatchState('isNewValid', isNewValid);
    };
    const updateIsVisible = (isVisible: boolean, ignoreIsVisibilityBlocked = false): void => {
      const isVisibilityBlocked = untracked(() => store.isVisibilityBlocked());
      if (!ignoreIsVisibilityBlocked && isVisibilityBlocked) return;

      patchState(store, state => ({ ...state, isVisible, isVisibilityBlocked: true }));
    };
    const unblockVisibility = (): void => patchState(store, state => ({ ...state, isVisibilityBlocked: false }));
    const updateSelectedTariff = (selectedTariff: TariffSelectionCardData): void => safePatchState('selectedTariff', selectedTariff);
    const addValidatedTariffKey = (tariffKey: string): void => {
      if (!tariffKey) return;
      const current = untracked(() => store.validatedTariffKeys());
      if (!current.includes(tariffKey)) {
        patchState(store, state => ({ ...state, validatedTariffKeys: [...current, tariffKey] }));
      }
    };
    const clearValidatedTariffKeys = (): void => patchState(store, state => ({ ...state, validatedTariffKeys: [] }));
    const updateSetError = (setError: boolean): void => patchState(store, state => ({ ...state, setError }));
    const updateSetWLError = (setWLError: boolean): void => patchState(store, state => ({ ...state, setWLError }));
    const resetCounter = (): void => patchState(store, state => ({ ...state, counter: 0 }));
    const updateCounter = (): void => patchState(store, state => ({ ...state, counter: store.counter() + 1 }));

    /** ===== Utility Functions ===== **/
    const checkOldVoucher = (voucherCode: string): boolean => {
      const numericalRegex = /^[0-9]+$/; // old vouchers only contain numbers
      return numericalRegex.test(voucherCode);
    };
    const handleSpecialCases = (ILError: ILError): boolean => {
      // used to convert a validation error into a detail error for correct error UI
      // see errors in testSpecificErrorVoucher()
      const code = ILError.error.code;
      return (
        code === '3008' || code === '3009' || code === '3010' || code === '3011' || code === '3012' || code === '3018' || code === '3030'
      );
    };
    const getILError = (error: HttpErrorResponse): ILError => {
      if (error?.error?.error) return error?.error; // try to get IL error
      if (error?.status) return { error: { code: error?.status?.toString(), message: error?.message } }; // if that is not possible get normal HTTP response

      return undefined;
    };
    const randomWait = async (): Promise<void> => {
      // a slight delay is unfortunately required as the (inputBlurred) event of FormComponent gets emitted even when clicking from
      // inside <input> to FormInputLoadingSpinnerComponent - this leads to two calls of asyncLoadingFunc which is not intended
      // adding a slight delay 1. improves UX as to make it seem a request was made and 2. avoids the aforementioned bug
      // eslint-disable-next-line
      await new Promise<void>(resolve => setTimeout(resolve, 100 + 150 * Math.random()));
    };
    const safePatchState = (name: string, value: object): void => {
      // creating new references for identical response is unfortunately required as the same response would otherwise not cause an update
      // it is also required to check for null, because { ...undefined } === {} !== undefined
      patchState(store, state => ({ ...state, [name]: value ? { ...value } : value }));
    };
    const safePatchStateExtention = (name: string, value: object): void => {
      patchState(store, state => ({ ...state, [name]: [...store[name](), value] }));
    };

    /** ===== Exposed Functions ===== **/
    return {
      /** ===== Service Functions ===== **/
      getVoucherDetail,
      getVoucherValidity,
      /** ===== Store Functions ===== **/
      resetStore,
      deleteStore,
      updateVoucherDetailError,
      updateVoucherDetailWarning,
      updateVoucherValidity,
      updateVoucherValidityError,
      updateVoucher,
      updateIsNewValid,
      causeIsNewValidUpdate,
      updateIsVisible,
      unblockVisibility,
      clearValidatedTariffKeys,
      updateSetError,
      updateSetWLError,
      resetCounter,
      updateCounter,
      /** ===== Utility Functions ===== **/
      checkOldVoucher,
      handleSpecialCases,
    };
  }),
  withProps(store => ({
    isValid$: toObservable(store.isNewValid).pipe(
      map(valid => valid.value),
      filter(valid => valid !== null),
      map(valid => valid === true || valid === undefined),
      tap(valid => {
        if (!valid) {
          store.updateSetError(!store.setError());
          store.updateSetWLError(!store.setWLError());
        }
      }),
    ), // to be used in old wizards
    isRequiredValid$: toObservable(store.isNewValid).pipe(
      map(valid => valid.value),
      filter(valid => valid !== null),
      map(valid => valid === true),
      tap(valid => {
        const errorCode = store.voucherDetailError()?.error?.code;

        if (!valid) {
          if (!errorCode) store.updateVoucherDetailError({ error: { code: 'required' } } as ILError);
          else store.updateSetError(!store.setError());
        }
      }),
    ), // to be used in old wizard
  })),
  // this sets up the store so that API calls are correctly send in for Tarif-Wechsel/Kunde-Werden
  // this is unfortunately necessary, as the first value is never set in TariffSelectionCardStoreComponent initially
  // that is also the reason why a counter is required
  withHooks(store => {
    return {
      onInit(): void {
        effect(() => {
          const selectedTariffs = store.tariffSelectionStore.selectedTariffs();

          if (selectedTariffs?.length === 0) return;
          if (store.counter() === 0) {
            store.updateCounter();
            store.tariffSelectionStore.setSelectedTariffsForNewVoucher(selectedTariffs);
          }
        });
      },
      onDestroy(): void {
        // this circumvents a bug when closing a wizard with a valid voucher
        store.deleteStore();
      },
    };
  }),
);
